import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, TreePine, RefreshCw, User, LogOut, CreditCard, ClipboardList, Flame, Bell } from "lucide-react";
import ProgramView from "@/components/ProgramView";
import ProgressView from "@/components/ProgressView";
import SessionView from "@/components/SessionView";
import BilanForm from "@/components/BilanForm";
import Chatbot from "@/components/Chatbot";
import { Logo } from "@/components/Logo";
import AccountSettings from "@/components/AccountSettings";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, checkAuth } = useAuth();
  const TABS = ["programme", "seance", "progres", "bilan", "profil"];
  const current = location.pathname.slice(1);
  const tab = TABS.includes(current) ? current : "programme";
  const setTab = (t) => navigate(`/${t}`);
  const [reloadKey, setReloadKey] = useState(0);
  const [hasQuestionnaire, setHasQuestionnaire] = useState(true);
  const [bilanDue, setBilanDue] = useState(false);

  useEffect(() => {
    apiClient.get("/questionnaire/latest").then(({ data }) => {
      if (!data || !data.goal) setHasQuestionnaire(false);
    }).catch(() => {});
    if (user?.subscription_status && user.subscription_status !== "none") {
      apiClient.post("/bilan/check-reminder").then(({ data }) => setBilanDue(!!data.due)).catch(() => {});
    }
    if (user?.subscription_status === "trial") {
      apiClient.post("/trial/reminder-check").catch(() => {});
    }
    checkAuth();
  }, []);

  const status = user?.subscription_status;
  const trialExpired = status === "trial" && user?.trial_ends_at && new Date(user.trial_ends_at) < new Date();
  const subscribed = status === "active" || (status === "trial" && !trialExpired);
  const trialDaysLeft = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000))
    : 0;

  const tabs = [
    { key: "programme", label: "Programme", icon: LayoutGrid },
    { key: "seance", label: "Séance", icon: Flame },
    { key: "progres", label: "Progrès", icon: TreePine },
    { key: "bilan", label: "Bilan", icon: RefreshCw },
    { key: "profil", label: "Profil", icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#0A0F0D] text-white pb-28">
      <header className="sticky top-0 z-40 glass border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Logo size="sm" />
        {subscribed ? (
          <span className="text-xs font-semibold text-lime-300 border border-lime-400/30 rounded-full px-3 py-1">
            {status === "trial" ? (trialDaysLeft > 0 ? `Essai · ${trialDaysLeft}j` : "Essai terminé") : user?.current_plan}
          </span>
        ) : (
          <button data-testid="header-subscribe-button" onClick={() => navigate("/pricing")}
            className="text-xs font-bold bg-lime-400 text-black rounded-full px-3 py-1.5">S'abonner</button>
        )}
      </header>

      <main className="max-w-md mx-auto sm:max-w-2xl px-4 py-5">
        {!hasQuestionnaire && tab !== "programme" && (
          <button data-testid="complete-questionnaire-banner" onClick={() => navigate("/questionnaire")}
            className="w-full mb-4 flex items-center gap-3 rounded-2xl border border-lime-400/30 bg-lime-400/[0.06] p-4 text-left">
            <ClipboardList className="h-5 w-5 text-lime-400" />
            <div>
              <p className="font-semibold text-sm">Complète ton questionnaire</p>
              <p className="text-xs text-white/50">Nécessaire pour générer ton programme</p>
            </div>
          </button>
        )}

        {bilanDue && tab !== "bilan" && (
          <button data-testid="bilan-due-banner" onClick={() => setTab("bilan")}
            className="w-full mb-4 flex items-center gap-3 rounded-2xl border border-lime-400/40 bg-lime-400/[0.08] p-4 text-left animate-float-up">
            <Bell className="h-5 w-5 text-lime-400" />
            <div>
              <p className="font-semibold text-sm">Ton bilan mensuel est disponible 🌿</p>
              <p className="text-xs text-white/50">Fais le point pour recevoir un programme ajusté</p>
            </div>
          </button>
        )}

        <h1 className="font-display text-2xl font-black mb-4 animate-float-up">
          {tab === "programme" && "Mon programme"}
          {tab === "seance" && "Séance du jour"}
          {tab === "progres" && "Ma progression"}
          {tab === "bilan" && "Mon bilan"}
          {tab === "profil" && "Mon profil"}
        </h1>

        <div key={tab} className="animate-float-up">
          {tab === "programme" && <ProgramView reloadKey={reloadKey} hasQuestionnaire={hasQuestionnaire} />}
          {tab === "seance" && <SessionView />}
          {tab === "progres" && <ProgressView />}
          {tab === "bilan" && <BilanForm subscribed={subscribed} onDone={() => { setReloadKey((k) => k + 1); setBilanDue(false); setTab("programme"); }} />}
          {tab === "profil" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-white/10 bg-[#141C17] p-5 flex items-center gap-4">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-14 w-14 rounded-full border-2 border-lime-400" data-testid="user-avatar" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-lime-400/15 flex items-center justify-center">
                    <User className="h-6 w-6 text-lime-400" />
                  </div>
                )}
                <div>
                  <p className="font-bold" data-testid="user-name">{user?.name}</p>
                  <p className="text-xs text-white/40" data-testid="user-email">{user?.email}</p>
                </div>
              </div>
              <AccountSettings />
              <div className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-lime-400" />
                    <span className="text-sm">Abonnement</span>
                  </div>
                  <span className={`text-sm font-semibold ${subscribed ? "text-lime-300" : "text-white/50"}`}>
                    {status === "trial" ? (trialDaysLeft > 0 ? `Essai · ${trialDaysLeft}j restants` : "Essai terminé") : subscribed ? user?.current_plan : "Aucun"}
                  </span>
                </div>
                {status !== "active" && (
                  <button onClick={() => navigate("/pricing")}
                    className="w-full mt-4 bg-lime-400 text-black font-bold py-2.5 rounded-xl">
                    {status === "trial" ? "Passer à un abonnement" : "Choisir une formule"}
                  </button>
                )}
              </div>
              <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }}
                className="w-full flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#141C17] py-3.5 text-sm font-semibold text-white/70 hover:text-white hover:border-white/25">
                <LogOut className="h-4 w-4" /> Se déconnecter
              </button>
              <p className="text-center text-xs text-white/30 px-4">
                Programme bien-être général, ne remplace pas l'avis d'un professionnel de santé.
              </p>
            </div>
          )}
        </div>
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10">
        <div className="max-w-md mx-auto grid grid-cols-5">
          {tabs.map((t) => (
            <button key={t.key} data-testid={`nav-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex flex-col items-center gap-1 py-3 transition-colors ${tab === t.key ? "text-lime-400" : "text-white/40"}`}>
              <t.icon className="h-5 w-5" />
              <span className="text-[10px] font-semibold">{t.label}</span>
            </button>
          ))}
        </div>
      </nav>
      <Chatbot />
    </div>
  );
}
